import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AppProvider } from './contexts/AppContext';
import App from './App';
import './styles/app.css';
import { initTelemetry } from './telemetry';

// Initialise browser OpenTelemetry before React renders
try { initTelemetry(); } catch (e) { console.warn('Telemetry init failed:', e); }

// Stash any portal-handoff token from URL BEFORE React/BrowserRouter mount.
// The router's catch-all `<Navigate to="/cases">` synchronously replaces the
// URL on first render and drops the fragment, so an `app.chargestack.ai/#token=...`
// load would lose the token before AuthContext's useEffect could read it.
// Pre-stashing into sessionStorage lets AuthContext pick it up regardless of
// what the router does to the URL. Same shape works for the legacy
// `?token=...` query-string form (kept as a fallback during portal rollout).
(function stashPortalToken() {
  try {
    const hashStr = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const hashParams = new URLSearchParams(hashStr);
    const searchParams = new URLSearchParams(window.location.search);
    const token = hashParams.get('token') || searchParams.get('token');
    const refresh = hashParams.get('refresh_token') || searchParams.get('refresh_token');
    if (!token) return;
    sessionStorage.setItem('dc_portal_token', token);
    if (refresh) sessionStorage.setItem('dc_portal_refresh_token', refresh);
    // Strip the token bits from the URL up-front (defence-in-depth — AuthContext
    // also re-strips, but doing it here means nothing else sees the JWT).
    hashParams.delete('token');
    hashParams.delete('refresh_token');
    searchParams.delete('token');
    searchParams.delete('refresh_token');
    const cleanSearch = searchParams.toString() ? '?' + searchParams.toString() : '';
    const cleanHash = hashParams.toString() ? '#' + hashParams.toString() : '';
    let nextUrl = window.location.pathname + cleanSearch + cleanHash;

    // If we stashed a return location before the portal round-trip (logout /
    // step-up re-auth), restore it as the INITIAL URL here — before
    // BrowserRouter mounts — so the router renders the right route. Doing it in
    // AuthContext's useEffect is too late: the catch-all `<Navigate to="/cases">`
    // has already rendered, and a bare `history.replaceState` changes the
    // address bar without making React Router navigate (the bug that landed the
    // user on /cases after step-up re-auth instead of back on their page).
    // Token-gated: only a portal hand-off carries a return-to round-trip.
    const returnTo = sessionStorage.getItem('dc_return_to');
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      sessionStorage.removeItem('dc_return_to');
      nextUrl = returnTo;
    }
    window.history.replaceState({}, '', nextUrl);
  } catch (e) {
    console.warn('Portal token pre-stash failed:', e);
  }
})();

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <App />
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>
);
